// The one add-a-reaction button and emoji picker, worn three ways: at the end
// of ReactionBar once a message has reactions, in the .message-actions toolbar
// while it has none, and as a row in the touch action sheet. One picker, one
// fetch path, one place to change the behaviour.
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import Picker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data';
import * as api from '../lib/api';
import { useMessageStore } from '../stores';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { MOBILE_VIEWPORT_QUERY } from '../hooks/useMobileViewport';
import { useToast } from './Toast';

interface Props {
  channelId: string;
  messageId: string;
  /** Drives the optimistic dispatch, and the rollback by user id that keeps a failure from deleting someone else's reaction. */
  currentUserId: string;
  /**
   * Which shape to render. inline-pill = the rounded pill that matches the
   * reaction row; toolbar-btn = the borderless small button that matches
   * edit/delete in .message-actions; sheet-row = a full-width labelled row in
   * the touch action sheet.
   */
  variant: 'inline-pill' | 'toolbar-btn' | 'sheet-row';
}

// A unit test pins this exact copy; move both together.
const ADD_REACTION_FAILED_TOAST = '添加 reaction 失败, 请重试';

// emoji-mart's default picker width, from perLine=9 at 36px a cell. Held as a
// constant so the flip decision never depends on the popover's first frame,
// where an async render can still measure zero.
const EMOJI_PICKER_WIDTH = 352;
const VIEWPORT_PADDING = 8;

export default function ReactionAddButton({ channelId, messageId, currentUserId, variant }: Props) {
  const addReactionOptimistic = useMessageStore(s => s.addReactionOptimistic);
  const removeReactionOptimistic = useMessageStore(s => s.removeReactionOptimistic);
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // null = not measured yet, held at visibility:hidden so the first frame does
  // not flash in the wrong place; 'left' / 'right' = the measured alignment;
  // 'mobile' = no JS alignment at all, the stylesheet's bottom sheet takes it.
  const [align, setAlign] = useState<'left' | 'right' | 'mobile' | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismissibleLayer({
    active: open,
    within: [pickerRef, btnRef],
    onEscape: close,
    onOutsidePress: close,
  });

  // Viewport flip. When the + sits at the message's right edge (the
  // .message-actions toolbar hugs it at right:16px), a popover opening along
  // the stylesheet's default left:0 is clipped by the right edge of the
  // viewport. Measure the anchor against the known picker width whenever the
  // popover opens or the viewport changes, and pick left / right / mobile
  // alignment from that. Deliberately no floating-ui or popper.js.
  useLayoutEffect(() => {
    if (!open) {
      setAlign(null);
      return;
    }
    if (typeof window === 'undefined') return;

    const measure = () => {
      // Two cases have no anchor worth flipping against and let the stylesheet's
      // bottom-sheet rules take over: a sheet row, which always spans the sheet,
      // and any viewport inside the mobile breakpoint. Marking them 'mobile'
      // keeps every left/right inline style off the popover so those rules,
      // which add no specificity, still win. The breakpoint is read through the
      // stylesheet's own query text so the two can't drift apart.
      if (variant === 'sheet-row' || window.matchMedia(MOBILE_VIEWPORT_QUERY).matches) {
        setAlign('mobile');
        return;
      }
      const btn = btnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      // A hidden-by-ancestor or detached node measures {0,0,0,0}. Never treat a
      // zero rect as a real position: keep the previous align (or null, which
      // holds the popover at visibility:hidden) and decide on the next measure.
      if (rect.width === 0 && rect.height === 0) return;
      const overflowsRight =
        rect.left + EMOJI_PICKER_WIDTH > window.innerWidth - VIEWPORT_PADDING;
      setAlign(overflowsRight ? 'right' : 'left');
    };

    measure();
    // Re-measure across a resize or rotation: a stale desktop inline
    // left/right:auto would otherwise beat the mobile rules.
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, variant]);

  // Design §4 #11 optimistic render + #11b race fix: dispatch optimistic add,
  // then await api.addReaction; on failure, remove by user_id instead of
  // deleting the whole emoji row, then showToast.
  const handlePickerSelect = async (emoji: { native: string }) => {
    if (busy) return;
    setOpen(false);
    setBusy(true);
    // ① Optimistic dispatch: show the new pill immediately.
    addReactionOptimistic(channelId, messageId, emoji.native, currentUserId);
    try {
      // ② Await server confirmation.
      await api.addReaction(messageId, emoji.native);
      // ③ Success: no local change. Server ack triggers WS UPDATE_REACTIONS
      // with the full row (server dedupes by user_id + emoji), so no duplicate pill is added.
    } catch {
      // ④ Failure: remove by user_id to avoid deleting another user's reaction, then toast.
      removeReactionOptimistic(channelId, messageId, emoji.native, currentUserId);
      showToast(ADD_REACTION_FAILED_TOAST);
    } finally {
      setBusy(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setOpen(v => !v);
  };

  const btnClass =
    variant === 'inline-pill'
      ? 'reaction-pill reaction-add'
      : variant === 'sheet-row'
        ? 'mobile-action-sheet-btn'
        : 'message-action-btn message-action-react';

  return (
    <span className="reaction-add-button-root">
      <button
        ref={btnRef}
        type="button"
        className={btnClass}
        onClick={handleClick}
        title="Add reaction"
        aria-label="Add reaction"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={busy}
        data-reaction-add-variant={variant}
      >
        <PlusOutlined />
        {variant === 'sheet-row' && ' React'}
      </button>
      {open && (
        <div
          className="reaction-picker-popover"
          ref={pickerRef}
          data-reaction-picker-align={align ?? 'measuring'}
          style={
            align === 'right'
              ? { left: 'auto', right: 0, visibility: 'visible' }
              : align === 'left'
                ? { left: 0, right: 'auto', visibility: 'visible' }
                : align === 'mobile'
                  ? { visibility: 'visible' }
                  : { visibility: 'hidden' }
          }
        >
          <Picker
            data={emojiData}
            onEmojiSelect={handlePickerSelect}
            locale="zh"
            previewPosition="none"
          />
        </div>
      )}
    </span>
  );
}
